import type { GetSubscriptionsStripeCheckoutSessionIdResponseBody } from '@calm-web/api-sdk/lib/app-api';

import { ApiRequest } from '@/hooks/api';

// After `checkout.confirm()` redirects to `/checkout/return`, the subscription
// is provisioned asynchronously by Stripe's fulfillment webhook. Poll the status
// endpoint to ride out that delay before surfacing an error, mirroring
// `fetchActiveSubscriptionWithPolling` for the legacy flow. The budget is
// `maxAttempts × pollIntervalMs` (defaults below ≈ 30s for the /checkout/return
// caller; callers may pass a larger budget).
export const CHECKOUT_SESSION_POLL_INTERVAL_MS = 1000;
export const CHECKOUT_SESSION_POLL_MAX_ATTEMPTS = 30;

type CheckoutSessionStatus = Pick<
	GetSubscriptionsStripeCheckoutSessionIdResponseBody,
	'fulfilled' | 'fulfillmentStatus'
>;

/**
 * Whether Calm has finished granting the purchase. A one-time (payment-mode) session reports its
 * grant in `fulfillmentStatus`. It is null for a subscription-mode session, and absent from
 * responses that predate the field, so both read `fulfilled`.
 */
export const isCheckoutSessionFulfilled = (data: CheckoutSessionStatus): boolean =>
	(data.fulfillmentStatus ?? null) === null ? data.fulfilled : data.fulfillmentStatus === 'fulfilled';

/**
 * A one-time purchase whose grant failed after the buyer was charged. Terminal: the purchase needs
 * repair by support, so restarting checkout would charge the buyer again.
 */
export const isOneTimeFulfillmentFailed = (data: CheckoutSessionStatus): boolean =>
	data.fulfillmentStatus === 'failed';

// Resolves after `ms`, or rejects early with an `AbortError` if `signal` aborts — so an unmounted
// caller stops waiting immediately instead of running out its poll budget in the background.
const wait = (ms: number, signal?: AbortSignal): Promise<void> =>
	new Promise((resolve, reject) => {
		if (signal?.aborted) {
			reject(signal.reason ?? new DOMException('Aborted', 'AbortError'));
			return;
		}
		// A `const` holder lets `onAbort` (declared before the timer) clear the timer without a
		// use-before-define / prefer-const bind cycle.
		const handles: { timer?: ReturnType<typeof setTimeout> } = {};
		const onAbort = () => {
			clearTimeout(handles.timer);
			reject(signal?.reason ?? new DOMException('Aborted', 'AbortError'));
		};
		handles.timer = setTimeout(() => {
			// Remove the abort listener on the normal resolve path so listeners don't accumulate on
			// the signal across a multi-attempt poll.
			signal?.removeEventListener('abort', onAbort);
			resolve();
		}, ms);
		signal?.addEventListener('abort', onAbort, { once: true });
	});

/**
 * Polls the checkout-session status endpoint until the session reaches a terminal
 * state, or the attempt budget is exhausted. Terminal states are `fulfilled`,
 * `expired`, the mirror-backed `failed` (a delayed-payment-method decline), and
 * `fulfillmentStatus: 'failed'` (a charged buyer whose grant needs repair).
 * Returns the last status payload either way. Pass `signal` (from an effect/unmount
 * cleanup) to stop polling promptly when the caller unmounts.
 */
export const pollCheckoutSessionStatus = async (
	apiRequest: ApiRequest,
	sessionId: string,
	{
		pollIntervalMs = CHECKOUT_SESSION_POLL_INTERVAL_MS,
		maxAttempts = CHECKOUT_SESSION_POLL_MAX_ATTEMPTS,
		signal,
	}: { pollIntervalMs?: number; maxAttempts?: number; signal?: AbortSignal } = {},
): Promise<GetSubscriptionsStripeCheckoutSessionIdResponseBody> => {
	let lastError: unknown;
	for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
		if (signal?.aborted) {
			throw signal.reason ?? new DOMException('Aborted', 'AbortError');
		}
		try {
			const { data } = await apiRequest<GetSubscriptionsStripeCheckoutSessionIdResponseBody>({
				service: 'api',
				// `sessionId` comes from `searchParams.get('session_id')` (caller-influenceable),
				// so encode it before interpolating into the path.
				endpoint: `subscriptions/stripe/checkout-session/${encodeURIComponent(sessionId)}`,
				method: 'GET',
				// Bust intermediate caches: the status flips to `fulfilled` mid-window
				// once the webhook lands, and a cached early response would otherwise
				// stick for the full retry budget.
				noCache: true,
			});

			if (
				isCheckoutSessionFulfilled(data) ||
				isOneTimeFulfillmentFailed(data) ||
				data.status === 'expired' ||
				data.status === 'failed' ||
				attempt >= maxAttempts
			) {
				return data;
			}
		} catch (err) {
			// Aborted (unmount) → stop now; don't swallow-and-retry it as a transient error
			// (parity with `fetchActiveSubscriptionWithPolling`).
			if (signal?.aborted) {
				throw err;
			}
			// A single transient error (e.g. a 503 while the webhook lands) must NOT collapse
			// the whole poll — that would drop /checkout/return to the restart screen on a
			// paid session. Swallow and retry until the budget is exhausted; only the final
			// attempt's failure propagates. (Mirrors `fetchActiveSubscriptionWithPolling`.)
			lastError = err;
			if (attempt >= maxAttempts) {
				throw err;
			}
		}

		await wait(pollIntervalMs, signal);
	}

	// Unreachable: the final iteration always returns or throws above. Satisfy the return type.
	throw lastError ?? new Error('pollCheckoutSessionStatus: exhausted without a result');
};
